// SOS mecânica: os tipos de problema, a distância até cada oficina, as
// oficinas perto e os links (WhatsApp, ligar, rota, local no mapa).

// `busca`: o que o app procura no Google Maps para cada problema.
export const TIPOS_DE_PROBLEMA = [
  { id: 'pneu', nome: 'Pneu', dica: 'borracharia', busca: 'borracharia de caminhão' },
  { id: 'motor', nome: 'Motor', dica: 'mecânica diesel', busca: 'mecânica diesel para caminhão' },
  { id: 'eletrica', nome: 'Elétrica', dica: 'auto elétrica', busca: 'auto elétrica de caminhão' },
  { id: 'freio', nome: 'Freio e ar', dica: 'freios', busca: 'oficina de freio de caminhão' },
  { id: 'suspensao', nome: 'Suspensão e molas', dica: 'molas', busca: 'suspensão e molas de caminhão' },
  { id: 'cambio', nome: 'Câmbio e embreagem', dica: 'câmbio', busca: 'câmbio e embreagem de caminhão' },
  { id: 'guincho', nome: 'Guincho', dica: 'reboque', busca: 'guincho pesado para caminhão' },
  { id: 'outro', nome: 'Outro', dica: 'oficina', busca: 'oficina mecânica de caminhão' },
];

export const tipoDoProblema = (id) => TIPOS_DE_PROBLEMA.find((t) => t.id === id) ?? null;

// O raio começa em 20 km e pode ser ampliado.
export const RAIOS_KM = [20, 50, 100];

// Distância em linha reta (km) entre dois pontos { latitude, longitude }.
export function distanciaKm(a, b) {
  const rad = (g) => (g * Math.PI) / 180;
  const dLat = rad(b.latitude - a.latitude);
  const dLon = rad(b.longitude - a.longitude);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.latitude)) * Math.cos(rad(b.latitude)) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

export function textoDistancia(km) {
  if (km < 1) return `${Math.max(100, Math.round((km * 1000) / 100) * 100)} m`;
  if (km < 10) return `${km.toFixed(1).replace('.', ',')} km`;
  return `${Math.round(km)} km`;
}

const temLocal = (o) => o.latitude !== null && o.latitude !== undefined && o.longitude !== null && o.longitude !== undefined;
const pontoDe = (o) => ({ latitude: Number(o.latitude), longitude: Number(o.longitude) });

// As oficinas de confiança que atendem o problema: as que estão até o raio,
// da mais perto para a mais longe, e (à parte) as que não têm o local
// cadastrado. Oficina sem tipos marcados atende tudo.
export function oficinasDeConfianca(lista, { tipo, local, raioKm }) {
  const servem = lista.filter((o) => !o.deleted_at && o.ativo !== false && (!o.tipos?.length || o.tipos.includes(tipo)));
  const perto = local
    ? servem.filter(temLocal)
      .map((o) => ({ ...o, distancia: distanciaKm(local, pontoDe(o)) }))
      .filter((o) => o.distancia <= raioKm)
      .sort((a, b) => a.distancia - b.distancia)
    : [];
  const semLocal = servem.filter((o) => !temLocal(o))
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' }));
  return { perto, semLocal };
}

// Tira da lista do Google as que já estão entre as de confiança (mesmo
// cadastro do Google ou mesmo telefone).
export function semAsDeConfianca(doGoogle, deConfianca) {
  const ids = new Set(deConfianca.map((o) => o.google_place_id).filter(Boolean));
  const telefones = new Set(deConfianca.map((o) => o.telefone).filter(Boolean));
  return doGoogle.filter((o) => !ids.has(o.google_place_id) && !(o.telefone && telefones.has(o.telefone)));
}

// Telefone brasileiro → "55" + DDD + número (10 ou 11 dígitos depois do 55).
// Aceita com ou sem +55, espaços, traços e parênteses. null se não der.
export function normalizarTelefone(texto) {
  let d = String(texto ?? '').replace(/\D/g, '');
  if (d.startsWith('0')) d = d.replace(/^0+/, '');
  if ((d.length === 12 || d.length === 13) && d.startsWith('55')) return d;
  if (d.length === 10 || d.length === 11) return `55${d}`;
  return null;
}

// Celular (tem 9 na frente do número): dá para chamar no WhatsApp.
export const ehCelular = (numero) => /^55\d{2}9\d{8}$/.test(numero ?? '');

// (48) 99999-0000 ou (48) 3333-0000
export function telefoneNaTela(numero) {
  const m = /^55(\d{2})(\d{4,5})(\d{4})$/.exec(numero ?? '');
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : numero ?? '';
}

export const linkDeLigar = (numero) => `tel:+${numero}`;
export const linkDoWhatsApp = (numero, texto = '') => `https://wa.me/${numero}${texto ? `?text=${encodeURIComponent(texto)}` : ''}`;
const coordenadas = (p) => `${Number(p.latitude).toFixed(6)},${Number(p.longitude).toFixed(6)}`;
export const linkDoLocal = (p) => `https://maps.google.com/?q=${coordenadas(p)}`;
export const linkDaRota = (p) => `https://www.google.com/maps/dir/?api=1&destination=${coordenadas(p)}`;

// A mensagem que o motorista manda ao titular pelo WhatsApp.
export function mensagemDoSos({ motorista, caminhao, tipo, local }) {
  const t = tipoDoProblema(tipo);
  return [
    `🆘 SOS mecânica — ${motorista}${caminhao ? ` (${caminhao})` : ''}`,
    `Problema: ${t ? `${t.nome} (${t.dica})` : tipo}`,
    local ? `Onde estou: ${linkDoLocal(local)}` : 'Não consegui pegar a localização.',
    'Mandado pelo HSA Frota.',
  ].join('\n');
}

// O local escrito num link do Google Maps (o "compartilhar" do Maps ou o
// endereço do navegador): "@-28.48,-48.78", "q=-28.48,-48.78",
// "!3d-28.48!4d-48.78"… Link curto (maps.app.goo.gl) não traz o local.
export function localDoLink(texto) {
  const s = decodeURIComponent(String(texto ?? ''));
  const num = '(-?\\d{1,3}(?:\\.\\d+)?)';
  const padroes = [
    new RegExp(`!3d${num}!4d${num}`),
    new RegExp(`@${num},\\s*${num}`),
    new RegExp(`[?&](?:q|query|ll|destination|center)=${num},\\s*${num}`),
    new RegExp(`^\\s*${num}\\s*,\\s*${num}\\s*$`),
  ];
  for (const p of padroes) {
    const m = p.exec(s);
    if (!m) continue;
    const latitude = Number(m[1]);
    const longitude = Number(m[2]);
    if (Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180) return { latitude, longitude };
  }
  return null;
}
